import type { ReactNode } from "react";

import { getThemeClass, getThemeComponentClasses } from "@/shared/lib/themeComponentClasses";

type SummaryMetricCardTheme =
  | "primary"
  | "success"
  | "warning"
  | "danger"
  | "teal"
  | "green"
  | "yellow"
  | "red";

interface SummaryMetricCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon?: ReactNode;
  theme?: SummaryMetricCardTheme;
  onClick?: () => void;
  trendDirection?: "up" | "down";
  descriptionMuted?: boolean;
}

const themeAliases: Record<SummaryMetricCardTheme, "primary" | "success" | "warning" | "danger"> = {
  primary: "primary",
  success: "success",
  warning: "warning",
  danger: "danger",
  teal: "primary",
  green: "success",
  yellow: "warning",
  red: "danger",
};

const themeClasses = {
  primary: {
    border: "border-theme-primary",
    background: "bg-theme-primary",
    text: "text-theme-primary",
  },
  success: {
    border: "border-theme-success",
    background: "bg-theme-success",
    text: "text-theme-success",
  },
  warning: {
    border: "border-theme-warning",
    background: "bg-theme-warning",
    text: "text-theme-warning",
  },
  danger: {
    border: "border-theme-danger",
    background: "bg-theme-danger",
    text: "text-theme-danger",
  },
} as const;

export default function SummaryMetricCard({
  title,
  value,
  description,
  icon,
  theme = "primary",
  onClick,
  trendDirection,
  descriptionMuted = false,
}: SummaryMetricCardProps) {
  const dashboardCardClasses = getThemeComponentClasses().dashboardCard;
  const normalizedTheme = themeAliases[theme];
  const currentTheme = themeClasses[normalizedTheme];

  return (
    <div
      onClick={onClick}
      className={`${getThemeClass(dashboardCardClasses.bgColor, "bg-theme-surface")} ${dashboardCardClasses.borderColor || currentTheme.border} ${dashboardCardClasses.base} flex h-full items-start justify-between rounded-xl border p-5 shadow-sm ${onClick ? "cursor-pointer transition hover:shadow-md" : ""}`}
    >
      <div className="flex h-full flex-col justify-between gap-4">
        <div>
          <p
            className={`mb-1 text-sm font-medium ${getThemeClass(
              dashboardCardClasses.mutedTextColor,
              "text-theme-text-soft"
            )}`}
          >
            {title}
          </p>

          <h2
            className={`text-2xl font-bold ${getThemeClass(
              dashboardCardClasses.textColor,
              "text-theme-text"
            )}`}
          >
            {value}
          </h2>
        </div>

        {description && (
          <p
            className={`text-xs font-medium flex items-center gap-1 ${
              descriptionMuted
                ? getThemeClass(dashboardCardClasses.mutedTextColor, "text-theme-text-soft")
                : currentTheme.text
            }`}
          >
            {trendDirection ? (
              <span className="font-bold">{trendDirection === "up" ? "↗" : "↘"}</span>
            ) : null}
            {description}
          </p>
        )}
      </div>

      {icon ? (
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-lg ${
            dashboardCardClasses.accentBgColor || currentTheme.background
          } ${getThemeClass(dashboardCardClasses.accentTextColor, "text-theme-text-inverse")}`}
        >
          {icon}
        </div>
      ) : null}
    </div>
  );
}
